import React, { useState, useContext } from "react";
import { useSelector, useDispatch } from 'react-redux';

// Atoms
import { Box, Checkbox, Stack, } from "@mui/material";
import Label from "../../../../common/components/Label";
import SearchAndSelectDropDown from "../../../../common/components/SearchAndSelectDropDown";
import { RotatingLines } from 'react-loader-spinner';


// Types
import { GetFolderDataType, LabelValueType } from "../../../../types/dataTypes";
import { RootState } from "../../../../redux/store";

// Constants
import { COLOR_MAP } from "../../../../constants/ColorNames";

// Styles
import styled from "@emotion/styled";

// Helpers
import { ToastContext } from "../../../../utils/context/toastContext";
import { dateFormatter } from "../../../../utils/helpers";

// Services
import { useLazyGetAlreadyContactedMailCountQuery, useGetContactableMailCountQuery, useGetFolderNameListMutation, usePostFolderDataMutation, useLazyGetAllFoldersQuery } from "../../../../redux/services/folderName";
import { setFolderName, setCandidateNo, setFolderId, setErrorMessages, setSendMailToContactedProfiles, setNonContactedCandidateCount, setCampaignCreationQueryData } from "../../../../redux/slice/campaignSlice";
import useDebounceValue from "../../../../hooks/useDebounceValue";
import ContactedProfileToggle from "./ContactedProfileToggle";
import CommonTooltip from "../../../../common/components/CommonTooltip";
import { INFO_ICON } from "../../../../constants/icons/InfoIcon";
import { campaignMaxLimitSelector } from "../../../../redux/selectors/user.selector";
// import { INFO_ICON } from "../../../../constants/icons/InfoIcon";


const MaxVal = styled.span`
    width:46%;
    background-color: ${COLOR_MAP.DISABLED};
    display: flex;
    font-size: 1.2rem;
    align-items: center;
    font-weight: 400;
    border:0.1rem 0.1rem 0.1rem 0;
    padding: 0.4rem 1.2rem 0.4rem 1.2rem;
    border-radius: 0 0.4rem 0.4rem 0;
    border: 0.1rem solid  ${COLOR_MAP.BORDER_GREY};
    border-left: none;
    text-align: center;
`

const MaxValueBulk = styled(MaxVal)`
  width:20%;
`

const CandidateInput = styled.input`
    width: 100%;
    background-color: ${COLOR_MAP.DISABLED};
    display: flex;
    font-size: 1.2rem;
    align-items: center;
    font-weight: 400;
    border:0.1rem;
    padding:1.2rem;
    border-radius: 0.4rem 0 0 0.4rem  ;
    border: 0.1rem solid ${COLOR_MAP.BORDER_GREY};

    &:focus {
     outline: none;
    }
`

const BulkCandidateInput = styled(CandidateInput)`
  width: 70%;
  background-color: ${COLOR_MAP.WHITE};
`

const Message = styled.p`
    font-weight:400 ;
    font-size: 1.2rem;
    line-height: 1.6rem;
    color: ${COLOR_MAP.LIGHT_GREY};
    margin: 0;
`

const ErrorMessage = styled(Message)`
    color: ${COLOR_MAP.FAILURE};
`
const OptionList = styled.li`
    padding: 0.4rem 1.2rem;
    margin-bottom: 0.8rem;
    flex-direction: column !important;
    align-items: flex-start !important;
`

const OptionLabel = styled.p`
    font-weight:400 ;
    font-size: 1.4rem;
    line-height: 2rem;
    color: ${COLOR_MAP.GREY_2};
    margin: 0;
`

const OptionLabelDate = styled.p`
    font-weight:400 ;
    font-size: 1.2rem;
    line-height: 1.6rem;
    color: ${COLOR_MAP.GREY_1};
    margin: 0;
`
const ContactableText = styled.p`
  position: absolute;
  top: 1.2rem;
  left: 1.2rem;
  font-weight:700 ;
  font-size: 1.4rem;
  line-height: 2rem;
  color: ${COLOR_MAP.GREY_1};
  margin: 0;
`
const StyledCheckBox = styled(Checkbox)`
  padding: 0;
  & svg {
    color: ${COLOR_MAP.GREY_2} !important;
    width: '1.4rem';
    height: '1.4rem'
  }
`

const options = (props: object, option: LabelValueType) => {
  return (
    <OptionList {...props}>
      <OptionLabel>{option.label}</OptionLabel>
      <OptionLabelDate>{option.date}</OptionLabelDate>
    </OptionList>
  )
}


const SelectedCandidatesInput = ({ contactableMailDetails }: any) => {

  if (!contactableMailDetails) return null;
  const { totalCount } = contactableMailDetails;

  return (
    <ContactableText>{totalCount}</ContactableText>
  )
}


const CandidateDetails = () => {
  const [folderInputValue, setFolderInputValue] = useState<string>('')

  const [ getFolderNameList , { data: foldersListData, isLoading: isFetchingFolders  }] = useGetFolderNameListMutation();
  const [postFolderData, { data: createdFolderId }] = usePostFolderDataMutation();
  const { setToastValue } = useContext(ToastContext)
  const dispatch = useDispatch();


  const folderName = useSelector((state: RootState) => state.campaign.folderName);
  const folderId = useSelector((state: RootState) => state.campaign.folderId);
  const candidateNo = useSelector((state: RootState) => state.campaign.candidateNo);
  const errorMessages = useSelector((state: RootState) => state.campaign.errorMessages);
  const userData = useSelector((state: RootState) => state.campaign.userData);
  const campaignQueryData = useSelector((state: RootState) => state.campaign.campaignCreationQueryData);
  const selectedContactType = useSelector((state: RootState) => state.campaign.selectedContactType);
  const allFolders = useSelector((state: RootState) => state.campaign.allFolders);
  const sendMailToContactedProfiles = useSelector((state: RootState) => state.campaign.sendMailToContactedProfiles)



  const [trigger, { data: contactedMailsCount }] = useLazyGetAlreadyContactedMailCountQuery();
  const { data: contactableMailDetails, isLoading, error: contactableMailError, isError: contactableMailIsError } = useGetContactableMailCountQuery(campaignQueryData.campaignInitiationId, {skip: !campaignQueryData.campaignInitiationId});
  const [bulkCampaignTotalCount, setBulkCampaignTotalCount] = useState(0);
  const campaignMaxLimit = useSelector(campaignMaxLimitSelector);
  


  const urlParams = new URLSearchParams(window.location.search);
  const profileCount = urlParams.get('profileCount'); 
  const totalProfileCount = (profileCount && +profileCount) ? Math.min(+profileCount, +campaignMaxLimit) : null

  const isBulkCampaign = campaignQueryData.isBulkCampaign === 'true' ? true : false;
  const triggeredFromPage = campaignQueryData.triggeredFromPage;

  const debouncedSearchValue = useDebounceValue(folderInputValue, 200);

  const folderNameHasNumber = folderName?.match(/\((\d+)\)/)
  const folderCount = (folderName && folderNameHasNumber ) ? folderNameHasNumber[1] : null;
  const totalCandidatesIncludingFolder = folderCount ? (bulkCampaignTotalCount + Number(folderCount)) : bulkCampaignTotalCount;
  const showContactedProfileOptions = isBulkCampaign && !triggeredFromPage;

  React.useEffect(() => {
    if(!contactableMailDetails) return;
      setBulkCampaignTotalCount(Math.min(contactableMailDetails?.totalCount, +campaignMaxLimit))
  },[contactableMailDetails, campaignMaxLimit])

  React.useEffect(() => {
    if(!showContactedProfileOptions || !contactableMailDetails) return;
    const { notContactableCount, totalCount, folderId } = contactableMailDetails || {};

    if(allFolders && allFolders.length > 0) {
      const selectedFolder = allFolders.filter((item: GetFolderDataType) => item.folderId === folderId);
      if(selectedFolder){
        dispatch(setFolderName({ folderName: selectedFolder[0]?.name }));
        dispatch(setFolderId({ folderId: selectedFolder[0]?.folderId }));
      }
  
    }

    if(selectedContactType === 'Non-contacted'){
      setBulkCampaignTotalCount(notContactableCount)
      if(Number(candidateNo) >  notContactableCount){
        dispatch(setErrorMessages({ errorMessages: {candidateInputNumber: 'The value entered is more than the max limit'}}))
      }
      else if((Number(candidateNo) <  notContactableCount) && errorMessages.candidateInputNumber){
        dispatch(setErrorMessages({ errorMessages: { candidateInputNumber: '' } })); 
      }
    }
    else{
      const total = Math.min(totalCount, +campaignMaxLimit)
      setBulkCampaignTotalCount(Math.min(total, +campaignMaxLimit))
      if(Number(candidateNo) >  total){
        dispatch(setErrorMessages({ errorMessages: {candidateInputNumber: 'The value entered is more than the max limit'}}))
      }
      else if((Number(candidateNo) <  total) && errorMessages.candidateInputNumber){
        dispatch(setErrorMessages({ errorMessages: { candidateInputNumber: '' } })); 
      }
    }
  },[showContactedProfileOptions, selectedContactType, contactableMailDetails, allFolders, campaignMaxLimit])

  React.useEffect(() => {
    debouncedSearchValue && getFolderNameList(debouncedSearchValue);
  },[debouncedSearchValue, getFolderNameList])

  React.useEffect(() => {
    if (contactableMailIsError) {
      setToastValue({
        isToastShown: true,
        toastMessage: (contactableMailError as any).data?.message || 'Error while fetching contactable mail counts',
        toastKey: 'contactable-details-fetch',
        toastSeverity: 'error'
      })
    }

  }, [contactableMailIsError, contactableMailError, setToastValue])

  React.useEffect(() => {
    if (contactableMailDetails && !isBulkCampaign){
        dispatch(setCandidateNo({ candidateNo: contactableMailDetails.totalCount }));
    }

  }, [contactableMailDetails, dispatch, isBulkCampaign])


  const createFolder = () => {
    postFolderData({ folderName, userData })
      .unwrap()
      .then((res) => {
        dispatch(setFolderId({ folderId: res }));
        getFolderNameList(folderInputValue);
        setToastValue({
          isToastShown: true,
          toastMessage: 'Folder Created !!',
          toastKey: 'folder-created-success',
          toastSeverity: 'success'
        })
        if (errorMessages.folderName) {
          dispatch(setErrorMessages({ errorMessages: { folderName: '' } }));
        }
      })
      .catch((err) => {
        setToastValue({
          isToastShown: true,
          toastMessage: err?.data?.message || 'Error while creating the folder',
          toastKey: 'folder-created-error',
          toastSeverity: 'error'
        })
      })
  }

  const toggleSendMailToContactedProfiles = () => {
    dispatch(setSendMailToContactedProfiles({ sendMailToContactedProfiles: !sendMailToContactedProfiles }))
  }


  const handleFolderNameChange = React.useCallback((newValue: string)=> {
    setFolderInputValue(newValue);
    dispatch(setFolderName({ folderName: newValue }));
    if(folderId){
      dispatch(setFolderId({ folderId: '' }));
    }

    if (errorMessages.folderName) {
      dispatch(setErrorMessages({ errorMessages: { folderName: '' } }));
    }

  },[dispatch, errorMessages.folderName, folderId])

  const handleCandidateDetailsChange = React.useCallback((newValue: string) => {
      dispatch(setCandidateNo({ candidateNo: newValue }));
      if(totalProfileCount ? Number(newValue) >  totalProfileCount : Number(newValue) >  bulkCampaignTotalCount){
        dispatch(setErrorMessages({ errorMessages: {candidateInputNumber: 'The value entered is more than the max limit'}}))
        return;
      }
      if (errorMessages.candidateInputNumber) {
        dispatch(setErrorMessages({ errorMessages: { candidateInputNumber: '' } }));
      }
  }, [bulkCampaignTotalCount, dispatch, errorMessages.candidateInputNumber, totalProfileCount])

  const handleCandidateSelection = React.useCallback((newValue: LabelValueType) => {

    if (newValue && newValue.label) {
      dispatch(setFolderName({ folderName: newValue?.label }));
      dispatch(setFolderId({ folderId: newValue?.value }));
      if (errorMessages.folderName) {
        dispatch(setErrorMessages({ errorMessages: { folderName: '' } }));
      }
    }
    else{
      dispatch(setFolderId({ folderId: '' }));
    }
  }, [dispatch, errorMessages.folderName])

  const updatedOptions = React.useCallback(() => {
    return foldersListData && foldersListData.length ? foldersListData.map((item: GetFolderDataType) => {
      let date = dateFormatter(item.folder_created);
      return {
        label: `${item.type} ${item.name} (${item.num_profiles})`,
        date: date,
        value: item.folderId
      }
    }
    )
      : [];
  }, [foldersListData])



const folderDisabledMessage = folderCount === '0' ? "No profiles found in the folder" : "Select a folder to enable this toggle" ;

  return (
    <Stack
      padding='1.6rem'
      rowGap='1rem'
      borderBottom={`0.1rem solid ${COLOR_MAP.BORDER_GREY}`}>
      <Stack>
        <Label
          colorType="dark"
          label="Candidate details"
        />

      </Stack>
      <Stack rowGap='0.3rem'>
        <Label
          colorType="light"
          label="Save in folder"
          required
          labelFor="saveInFolder"
        />
        <SearchAndSelectDropDown
          id="saveInFolder"
          inputValue={folderName}
          label="folderName"
          onCreateClick={createFolder}
          disabled={showContactedProfileOptions}
          onInputChange={(e, value) => handleFolderNameChange(value)}
          onValueSelect={(_, value) => handleCandidateSelection(value)}
          options={updatedOptions() || []}
          // selectedValue={selectedValue}
          value='id'
          placeholder="Search by folder name"
          renderOption={options}
          showError={Boolean(errorMessages.folderName)}
          errorMessage={errorMessages.folderName}
          isFetchingFolders={isFetchingFolders}
        />
      </Stack>
      {
        isBulkCampaign ?
          <>
            <Stack>
              <Stack marginBottom={'3px'}>
              <Label
                colorType="light"
                label="Select candidates"
                labelFor="selectCandidate"
              />
              </Stack>
              <Stack direction='row'>
                <BulkCandidateInput
                  id="selectCandidate"
                  type="text"
                  inputMode="numeric"
                  onChange={(e) => handleCandidateDetailsChange(e.target.value)}
                  value={candidateNo}
                />
                {
                  isLoading ?
                    <MaxVal>
                      Fetching
                      <RotatingLines
                        strokeColor="black"
                        strokeWidth="5"
                        animationDuration="0.75"
                        width="16"
                        visible={true}
                      />
                    </MaxVal>
                    :
                    <MaxValueBulk>
                      Max Value {totalProfileCount ? totalProfileCount :  bulkCampaignTotalCount}
                    </MaxValueBulk>
                }
              </Stack>
              {showContactedProfileOptions?
              <Stack
                  alignItems='center'
                  direction='row'
                  columnGap='0.5rem'>
                  <ContactedProfileToggle disabled={!folderId || folderCount==='0'} disabledMessage={folderDisabledMessage}/>
                </Stack>
              : null 
              }
            </Stack>

            {triggeredFromPage?.toLowerCase() === "srp" && <Stack
                  alignItems='center'
                  direction='row'
                  columnGap='0.5rem'
                  onClick = {toggleSendMailToContactedProfiles}
                  sx={{
                    cursor:'pointer'
                  }}
                >
                  <StyledCheckBox checked={sendMailToContactedProfiles} />
                  <Message>
                    Send campaign to existing profiles in the folder
                  </Message>

                  <CommonTooltip title={"Select this option to send campaign to profiles that already exist in the selected folder."} offset={[80,0]} width="300px" >
                      <Box sx={{display:"flex", alignItems:"center"}}><INFO_ICON height={14} width={14}/></Box>
                  </CommonTooltip>
                </Stack> 
            }
            {(errorMessages.candidateInputNumber) ?
              <Stack
                alignItems='center'
                direction='row'
                columnGap='0.5rem'>
                <ErrorMessage>
                  {errorMessages.candidateInputNumber}
                </ErrorMessage>
              </Stack>
            :
              null
            }
          </>
          :
          <>
            <Stack>
              <Stack marginBottom={'3px'}>
              <Label
                colorType="light"
                label="Select contactable candidates"
                labelFor="selectContactableCandidate"
              />
              </Stack>
              <Stack direction='row' position={'relative'}>
                <CandidateInput
                  id="selectContactableCandidate"
                  disabled={true}
                />
                <SelectedCandidatesInput contactableMailDetails={contactableMailDetails} />
              </Stack>
           {showContactedProfileOptions?
              <Stack
                  alignItems='center'
                  direction='row'
                  columnGap='0.5rem'>
                  <ContactedProfileToggle disabled={!folderId || folderCount==='0'} disabledMessage={folderDisabledMessage}/>
                </Stack>
              : null  
              }
            </Stack>
          </>
      }
    </Stack>
  )
}

export default CandidateDetails;